import { useState } from 'react';
import { ArrowLeft, Syringe, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, InsulinEntryDto } from '../services/health-data.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface InsulinTrackerProps {
  onBack: () => void;
}

export function InsulinTracker({ onBack }: InsulinTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [type, setType] = useState('');
  const [units, setUnits] = useState('');
  const [injectionSite, setInjectionSite] = useState('');
  const [glucoseBefore, setGlucoseBefore] = useState('');
  const [glucoseAfter, setGlucoseAfter] = useState('');
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);

  const insulinTypes = [
    'rapid-acting',
    'short-acting',
    'intermediate-acting',
    'long-acting',
    'ultra-long-acting',
    'premixed'
  ];

  const injectionSites = ['abdomen', 'thigh', 'arm', 'buttock', 'other'];

  // Fetch insulin entries
  const {
    data: entriesData,
    isLoading: entriesLoading,
  } = useQuery({
    queryKey: ['insulin', user?.id],
    queryFn: () => healthDataService.getInsulinEntries({ limit: 50 }),
    enabled: !!user,
  });

  const entries = entriesData?.data || entriesData || [];

  // Create insulin entry mutation
  const createMutation = useMutation({
    mutationFn: (data: InsulinEntryDto) => healthDataService.createInsulinEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insulin'] });
      toast.success('Insulin entry logged successfully!');
      setType('');
      setUnits('');
      setInjectionSite('');
      setGlucoseBefore('');
      setGlucoseAfter('');
      setNotes('');
      setShowForm(false);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!type || !units || isNaN(Number(units))) {
      toast.error('Please enter insulin type and valid units');
      return;
    }

    createMutation.mutate({
      type: type.trim(),
      units: Number(units),
      injectionSite: injectionSite || undefined,
      glucoseBefore: glucoseBefore ? Number(glucoseBefore) : undefined,
      glucoseAfter: glucoseAfter ? Number(glucoseAfter) : undefined,
      notes: notes.trim() || undefined,
    });
  };

  if (entriesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Insulin Tracking</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-dropsugar-blue to-dropsugar-blue-dark text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all mb-8"
          >
            + Add Insulin Entry
          </button>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">New Insulin Entry</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insulin Type *
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                  required
                >
                  <option value="">Select type</option>
                  {insulinTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Units *
                </label>
                <input
                  type="number"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                  placeholder="Enter units"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                  required
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Injection Site (Optional)
                </label>
                <select
                  value={injectionSite}
                  onChange={(e) => setInjectionSite(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                >
                  <option value="">Select site</option>
                  {injectionSites.map((site) => (
                    <option key={site} value={site}>
                      {site.charAt(0).toUpperCase() + site.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Glucose Before (mg/dL)
                  </label>
                  <input
                    type="number"
                    value={glucoseBefore}
                    onChange={(e) => setGlucoseBefore(e.target.value)}
                    placeholder="Optional"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Glucose After (mg/dL)
                  </label>
                  <input
                    type="number"
                    value={glucoseAfter}
                    onChange={(e) => setGlucoseAfter(e.target.value)}
                    placeholder="Optional"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Entry'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setType('');
                    setUnits('');
                    setInjectionSite('');
                    setGlucoseBefore('');
                    setGlucoseAfter('');
                    setNotes('');
                  }}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Syringe className="w-6 h-6 mr-2 text-dropsugar-green" />
            Recent Entries
          </h2>
          {entries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No insulin entries yet. Add your first entry above!</p>
          ) : (
            <div className="space-y-3">
              {entries.map((entry: any) => {
                const date = new Date(entry.takenAt || entry.createdAt);
                return (
                  <div key={entry.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-xl font-bold text-gray-900">
                            {entry.type.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join('-')}
                          </span>
                          <span className="text-lg text-gray-600">{entry.units} units</span>
                        </div>
                        {entry.injectionSite && (
                          <span className="inline-block mt-1 px-2 py-1 bg-dropsugar-green/20 text-dropsugar-green-dark rounded text-xs font-semibold">
                            {entry.injectionSite}
                          </span>
                        )}
                        {(entry.glucoseBefore || entry.glucoseAfter) && (
                          <div className="mt-2 text-sm text-gray-600">
                            {entry.glucoseBefore && <span>Before: {entry.glucoseBefore} mg/dL</span>}
                            {entry.glucoseBefore && entry.glucoseAfter && <span> • </span>}
                            {entry.glucoseAfter && <span>After: {entry.glucoseAfter} mg/dL</span>}
                          </div>
                        )}
                        <p className="text-sm text-gray-600 mt-1">
                          {date.toLocaleDateString()} {date.toLocaleTimeString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-gray-500 mt-1">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
